<script setup lang="ts">
/**
 * ONE MODAL DIALOG.
 *
 * There were eight: seven in the POS and the check drawer in the console. Every
 * one of them got `role="dialog" aria-modal="true"` right — `apps/pos/test/ui.spec.ts`
 * asserts that and has for a long time — and not one of them did anything about
 * the keyboard. Opening the tender dialog left focus on the button behind the
 * backdrop, Tab walked into the check panel underneath, and there was no Escape
 * handler anywhere in the product.
 *
 * `aria-modal="true"` is a PROMISE to assistive technology that the rest of the
 * document is inert. Making that promise while Tab still reaches the rest of the
 * document is worse than not making it: a screen-reader user is told the dialog
 * is the world, and then leaves it without being told they have.
 *
 * WHAT IT DELIBERATELY DOES NOT DO.
 *
 * No `<Teleport>`. `PosScreen.vue` refused one on the grounds that a portal only
 * adds a focus-management problem, and with a real focus trap and a z-index
 * scale that reasoning still holds — the dialog no longer needs to escape its
 * stacking context to sit above the page.
 *
 * It takes `label` as an ALREADY TRANSLATED string. This package holds no copy
 * and no translator, because the four applications do not agree on what a
 * translator is: two pass `(key) => string`, the kitchen screen passes a
 * `Translator` instance. A component that took a key would have to pick one.
 */

import { onBeforeUnmount, onMounted, ref } from "vue";

const props = withDefaults(
  defineProps<{
    /** The accessible name. Already translated — see the note above. */
    label: string;
    wide?: boolean;
    /**
     * Escape closes. Default true, because the ARIA authoring practices expect
     * it and because a keyboard user with no pointer has no other way out.
     *
     * Set false only where there is genuinely no cancel path — never merely to
     * make a decision feel weightier. A dialog nobody can leave is not a
     * stronger confirmation, it is a trap.
     */
    closeOnEscape?: boolean;
    /** Clicking the backdrop closes. Default FALSE — see the note below. */
    dismissible?: boolean;
    /**
     * Where focus lands.
     *
     * `"dialog"` — the container itself, so a screen reader reads the dialog's
     * name and then its content from the top. This is the default and it is the
     * decision `AgeCheckDialog.vue` had already made for itself and written
     * down: putting focus straight onto a confirm button invites somebody to
     * press space before they have read what they are confirming.
     *
     * `"first"` — the first focusable child. Correct for a dialog whose whole
     * purpose is one field to type in.
     */
    initialFocus?: "dialog" | "first";
  }>(),
  { wide: false, closeOnEscape: true, dismissible: false, initialFocus: "dialog" },
);

const emit = defineEmits<{ close: [] }>();

const dialog = ref<HTMLElement | null>(null);

/**
 * Who had focus before this opened, so it can be given back.
 *
 * Without this, closing a dialog drops focus to `<body>` and a keyboard user
 * restarts from the top of the page — which on the till means tabbing past the
 * whole product grid to get back to the button they just pressed.
 */
let restoreTo: HTMLElement | null = null;

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const focusable = (): HTMLElement[] => {
  const root = dialog.value;
  if (!root) return [];
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    // `offsetParent` is null for anything `display: none`, which is how a
    // conditional section inside a dialog would otherwise become a tab stop
    // that focuses nothing visible.
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
};

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape" && props.closeOnEscape) {
    event.stopPropagation();
    emit("close");
    return;
  }

  if (event.key !== "Tab") return;

  const stops = focusable();
  if (stops.length === 0) {
    // Nothing to move to, so the only correct behaviour is to stay put.
    // Letting Tab through here is exactly the bug: it leaves the dialog.
    event.preventDefault();
    return;
  }

  const first = stops[0]!;
  const last = stops[stops.length - 1]!;
  const active = document.activeElement;

  /**
   * The wrap, in both directions.
   *
   * `dialog.value.contains(active)` rather than `active === first`: focus may
   * be on the container itself (the default), in which case a plain Tab should
   * enter the dialog at the top and a Shift+Tab should enter it at the bottom,
   * not leave.
   */
  if (event.shiftKey) {
    if (active === first || active === dialog.value) {
      event.preventDefault();
      last.focus();
    }
    return;
  }

  if (active === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Body scroll, held still while a dialog is open.
 *
 * A counter rather than a boolean because the till can legitimately stack one:
 * the receipt appears over the tender dialog. Restoring on the first close
 * would let the page behind scroll while a dialog is still up.
 */
let previousOverflow = "";

function lockScroll(): void {
  const body = document.body;
  const depth = Number(body.dataset["gfDialogDepth"] ?? "0");
  if (depth === 0) {
    previousOverflow = body.style.overflow;
    body.style.overflow = "hidden";
  }
  body.dataset["gfDialogDepth"] = String(depth + 1);
}

function unlockScroll(): void {
  const body = document.body;
  const depth = Number(body.dataset["gfDialogDepth"] ?? "1") - 1;
  if (depth <= 0) {
    delete body.dataset["gfDialogDepth"];
    body.style.overflow = previousOverflow;
    return;
  }
  body.dataset["gfDialogDepth"] = String(depth);
}

onMounted(() => {
  restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  lockScroll();
  document.addEventListener("keydown", onKeydown, true);

  const target = props.initialFocus === "first" ? (focusable()[0] ?? dialog.value) : dialog.value;
  target?.focus();
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown, true);
  unlockScroll();
  // `isConnected` — the trigger may itself have been removed by whatever the
  // dialog did, and focusing a detached node silently does nothing while
  // looking like it worked.
  if (restoreTo?.isConnected) restoreTo.focus();
});

/**
 * The backdrop closes only if asked, and only on the backdrop itself.
 *
 * Default false, because most of these dialogs are decisions rather than
 * disclosures: a tender in progress, an age check, a void reason. Losing one to
 * a stray tap on a busy till is a worse failure than having to press cancel.
 * `event.target === event.currentTarget` so a click that began inside the
 * dialog and ended on the backdrop — a drag over a long list — does not count.
 */
function onScrimClick(event: MouseEvent): void {
  if (props.dismissible && event.target === event.currentTarget) emit("close");
}
</script>

<template>
  <div class="gf-scrim" @click="onScrimClick">
    <div
      ref="dialog"
      class="gf-dialog"
      :class="{ 'gf-dialog--wide': wide }"
      role="dialog"
      aria-modal="true"
      :aria-label="label"
      tabindex="-1"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
/*
 * The entrance, and why it is this small.
 *
 * 160 ms and eight pixels. A dialog that slides a long way on a till is a
 * dialog somebody is waiting for during a rush; the movement exists only to
 * say where the thing came from, and then to be over. `--gf-motion-base` is
 * zero under `prefers-reduced-motion`, which makes this a cut rather than a
 * fade — which is the correct reduced-motion behaviour, not a shorter slide.
 */
.gf-dialog {
  animation: gf-dialog-in var(--gf-motion-base) var(--gf-ease-out);
}

@keyframes gf-dialog-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* Focusing the container is how a screen reader starts at the top. It should
   not draw a ring around the whole dialog for a pointer user. */
.gf-dialog:focus {
  outline: none;
}

.gf-dialog:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: calc(var(--gf-focus-offset) * -1);
}
</style>
